<script setup lang="ts">
import type { ButtonProps } from '@/types/button'
import AppLink from '@/components/shared/AppLink.vue'
import ButtonSpinner from '@/components/shared/ButtonSpinner.vue'
import Icon from '@/components/shared/Icon.vue'
import { computed } from 'vue'
import { tv, type VariantProps } from 'tailwind-variants'

const buttonVariants = tv({
  base: [
    // base
    'relative isolate inline-flex items-center justify-center text-sm font-medium transition duration-300 ease-[cubic-bezier(0.4,0.36,0,1)] rounded-md shadow-[0_1px_theme(colors.white/0.07)_inset,0_1px_3px_theme(colors.gray.900/0.2)] border antialiased',
    // pseudo elements
    'before:duration-300 before:ease-[cubic-bezier(0.4,0.36,0,1)] before:transition-opacity',
    'before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:rounded-md before:bg-gradient-to-b before:from-white/20 before:opacity-50',
    'hover:before:opacity-100',
    'after:pointer-events-none after:absolute after:inset-0 after:-z-10 after:rounded-md after:bg-gradient-to-b after:from-white/10 after:from-[46%] after:to-[54%] after:mix-blend-overlay',
    // disabled
    'disabled:opacity-50 disabled:cursor-not-allowed',
    // dark shadows
    'dark:shadow-[0_1px_theme(colors.white/0.05)_inset,0_1px_3px_theme(colors.gray.950/0.4)]',
    // focus
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-900',
  ],
  variants: {
    variant: {
      primary: [
        // background and border
        'bg-primary-500 text-white border-primary-500',
        // dark mode
        'dark:ring-0 dark:bg-primary-600 dark:border-primary-700 dark:hover:bg-primary-500 dark:hover:border-primary-600',
        // focus
        'focus-visible:ring-primary-500/50 dark:focus-visible:ring-primary-400/50',
      ],
      secondary: [
        // background and border
        'bg-white text-primary-800 border-gray-300',
        // dark mode
        'dark:ring-0 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100',
        // focus
        'focus-visible:ring-gray-400/50 dark:focus-visible:ring-gray-500/50',
      ],
      ghost: [
        // base
        'shadow-none',
        // border
        'border-transparent',
        // text color
        'text-gray-900 dark:text-gray-50',
        // hover color
        'bg-transparent hover:bg-gray-100 dark:hover:bg-gray-800/80',
        // hide gradient pseudo-elements
        'before:hidden after:hidden',
        // disabled
        'disabled:text-gray-400',
        'disabled:dark:text-gray-600',
      ],
      destructive: [
        // text color
        'text-white',
        // border
        'border-transparent',
        // background color
        'bg-error-500 dark:bg-error-600',
        // hover color
        'hover:bg-error-600 dark:hover:bg-error-500',
        // disabled
        'disabled:bg-error-300 disabled:text-white',
        'disabled:dark:bg-error-950 disabled:dark:text-error-400',
      ],
      error: [
        // background and border
        'bg-error-500 text-white border-error-500 focus:ring-error-300',
        // dark mode
        'dark:ring-0 dark:bg-error-600 dark:border-error-700 dark:hover:bg-error-500 dark:hover:border-error-600',
        // focus
        'focus-visible:ring-error-500/50 dark:focus-visible:ring-error-400/50',
      ],
      success: [
        // background and border
        'bg-success-600 text-white border-success-600 focus:ring-success-300',
        // dark mode
        'dark:ring-0 dark:bg-success-700 dark:border-success-800 dark:hover:bg-success-600 dark:hover:border-success-700',
        // focus
        'focus-visible:ring-success-500/50 dark:focus-visible:ring-success-400/50',
      ],
      'text-white': [
        // transparent background with white text
        'bg-transparent text-white border-transparent shadow-none before:hidden after:hidden hover:underline',
        // dark mode
        'dark:text-gray-100',
        // focus
        'focus-visible:ring-white/50 dark:focus-visible:ring-gray-100/50',
      ],
      'text-primary': [
        // transparent background with primary text
        'bg-transparent text-primary-500 border-transparent shadow-none before:hidden after:hidden hover:underline',
        // dark mode
        'dark:text-primary-400 dark:hover:text-primary-300',
        // focus
        'focus-visible:ring-primary-500/50 dark:focus-visible:ring-primary-400/50',
      ],
    },
    size: {
      xs: 'px-2 pt-[0.125rem] pb-[0.1875rem] text-xs',
      sm: 'px-3 py-[3px]',
      md: 'px-2.5 py-[5px]',
      lg: 'px-3 py-2',
      square: 'pt-[0.125rem] pb-[0.125rem] px-[0.185rem]',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'md',
  },
})

const props = withDefaults(defineProps<ButtonProps>(), {
  htmlType: 'button',
  type: 'primary',
  size: 'md',
})

defineEmits<{
  click: [event: MouseEvent]
}>()

const buttonClass = computed(() => {
  return buttonVariants({
    variant: props.type as VariantProps<typeof buttonVariants>['variant'],
    size: props.size as VariantProps<typeof buttonVariants>['size'],
  })
})

const dropdownIconSize = computed(
  () =>
    ({
      xs: 'w-3 h-3',
      sm: 'w-[18px] h-[18px]',
      md: 'w-5 h-5',
      lg: 'w-5 h-5',
      square: 'w-6 h-6',
    })[props.size],
)

const showSpinner = computed(
  () => props.loading && !['text-white', 'text-primary'].includes(props.type),
)
</script>

<template>
  <AppLink
    v-if="href"
    class="app-link"
    :to="href"
    :class="buttonClass"
    :force-external="forceExternal"
  >
    <slot />
    <Icon v-if="dropdown" variant="chevron-down" class="ml-1.5 w-5 h-5" :stroke-width="2" />
  </AppLink>

  <button
    v-else
    :type="htmlType"
    :disabled="loading || disabled"
    :class="buttonClass"
    :aria-busy="loading"
    @click="(event) => $emit('click', event)"
  >
    <ButtonSpinner v-if="showSpinner" :size="size" class="mr-2" />

    <slot />

    <Icon v-if="dropdown" variant="chevron-down" :class="[dropdownIconSize]" :stroke-width="2" />
  </button>
</template>

<style scoped lang="postcss">
button > * {
  @apply pointer-events-none;
}
</style>
